import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, Play, Edit, Trash2, ArrowUpDown, WifiOff, Bell } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useScripts, Script } from "@/hooks/useScripts";
import { useOfflineDetection } from "@/hooks/useOfflineDetection";
import { useOfflineStorage } from "@/hooks/useOfflineStorage";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { OfflineScriptBadge } from "@/components/pwa/OfflineScriptBadge";
import { ScriptReminderDialog } from "@/components/pwa/ScriptReminderDialog";
import { SubscriptionRequiredModal } from "@/components/modals/SubscriptionRequiredModal";
import { formatDistanceToNow, Locale } from "date-fns";
import { zhTW, zhCN, enUS, ja, ko } from "date-fns/locale";

type SortKey = "title" | "scene_type" | "word_count" | "updated_at";
type SortOrder = "asc" | "desc";

export function ScriptsTable() {
  const { t, i18n } = useTranslation();
  
  const locales: Record<string, Locale> = {
    "zh-TW": zhTW,
    "zh-CN": zhCN,
    "en": enUS,
    "ja": ja,
    "ko": ko,
  };

  const navigate = useNavigate();
  const { scripts, deleteScript, loading } = useScripts();
  const { isOffline } = useOfflineDetection();
  const { cachedScripts, isScriptCached } = useOfflineStorage();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("updated_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [reminderScript, setReminderScript] = useState<{ id: string; title: string } | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { isTrialExpired } = useTrialStatus();
  const itemsPerPage = 10;

  // When offline, show only cached scripts
  const availableScripts = isOffline 
    ? cachedScripts.map(cs => ({
        id: cs.id,
        title: cs.title,
        content: cs.content,
        scene_type: cs.scene_type,
        word_count: cs.word_count,
        settings: cs.settings as Record<string, unknown>,
        updated_at: cs.cachedAt.toISOString(),
        created_at: cs.cachedAt.toISOString(),
      }))
    : scripts;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const filteredScripts = availableScripts.filter((script) =>
    script.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedScripts = [...filteredScripts].sort((a, b) => {
    let comparison = 0;
    switch (sortKey) {
      case "title":
        comparison = a.title.localeCompare(b.title);
        break;
      case "scene_type":
        comparison = a.scene_type.localeCompare(b.scene_type);
        break;
      case "word_count":
        comparison = a.word_count - b.word_count;
        break;
      case "updated_at":
        comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        break;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const totalPages = Math.ceil(sortedScripts.length / itemsPerPage);
  const paginatedScripts = sortedScripts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getSceneTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      recording: "bg-accent-yellow",
      speech: "bg-accent-cyan",
      meeting: "bg-accent-pink",
      podcast: "bg-accent-blue",
    };
    return (
      <Badge className={`${colors[type] || "bg-muted"} text-foreground border-foreground`}>
        {t(`dashboard.sceneTypes.${type}`)}
      </Badge>
    );
  };

  const SortableHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <Button
      variant="ghost"
      className="h-auto p-0 hover:bg-transparent"
      onClick={() => handleSort(sortKeyName)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-4 w-4" />
      </span>
    </Button>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-black shadow-[4px_4px_0_#1A1A1A]">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>{t("dashboard.scripts.title")}</CardTitle>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("dashboard.scripts.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {paginatedScripts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? t("dashboard.scripts.noResults") : t("dashboard.scripts.empty")}
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {paginatedScripts.map((script) => (
                <div 
                  key={script.id} 
                  className="border-2 border-black rounded-xl bg-white p-4 space-y-3"
                >
                  {/* Title Row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base truncate">{script.title}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {getSceneTypeBadge(script.scene_type)}
                        {isScriptCached(script.id) && (
                          <Badge variant="outline" className="gap-1 text-[10px] h-5 px-1.5 border-muted-foreground/30 text-muted-foreground">
                            <WifiOff className="h-2 w-2" />
                            {t('offline.availableOffline')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Info Row */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{script.word_count.toLocaleString()} {t("dashboard.scripts.columns.words")}</span>
                    <span>{formatDistanceToNow(new Date(script.updated_at), { addSuffix: true, locale: locales[i18n.language] || enUS })}</span>
                  </div>
                  
                  {/* Actions Row */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#6FEDD6] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                            onClick={() => setReminderScript({ id: script.id, title: script.title })}
                          >
                            <Bell className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t("notifications.reminder.title")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#4DA6FF] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                      onClick={() => navigate(`/editor/${script.id}`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                      onClick={() => {
                        if (isTrialExpired) {
                          setShowSubscriptionModal(true);
                        } else {
                          navigate(`/teleprompter/${script.id}`);
                        }
                      }}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-9 w-9 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("dashboard.scripts.deleteConfirm.title")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("dashboard.scripts.deleteConfirm.description")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("dashboard.scripts.deleteConfirm.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteScript(script.id)}>
                            {t("dashboard.scripts.deleteConfirm.confirm")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block border-2 border-black rounded-xl overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-100 border-b-2 border-black">
                  <TableRow>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("dashboard.scripts.columns.title")} sortKeyName="title" />
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("dashboard.scripts.columns.sceneType")} sortKeyName="scene_type" />
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("dashboard.scripts.columns.words")} sortKeyName="word_count" />
                    </TableHead>
                    <TableHead className="font-bold text-black">
                      <SortableHeader label={t("dashboard.scripts.columns.lastEdited")} sortKeyName="updated_at" />
                    </TableHead>
                    <TableHead className="text-right font-bold text-black">{t("dashboard.scripts.columns.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y-2 divide-black bg-white">
                {paginatedScripts.map((script) => (
                  <TableRow key={script.id} className="group hover:bg-muted/50 transition-colors">
                    <TableCell className="font-medium min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[300px]">{script.title}</span>
                        {isScriptCached(script.id) && (
                          <Badge variant="outline" className="gap-1 text-[10px] h-5 px-1.5 border-muted-foreground/30 text-muted-foreground">
                            <WifiOff className="h-2 w-2" />
                            {t('offline.availableOffline')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-middle">{getSceneTypeBadge(script.scene_type)}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground whitespace-nowrap">
                      {script.word_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                      {formatDistanceToNow(new Date(script.updated_at), { addSuffix: true, locale: locales[i18n.language] || enUS })}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex justify-end gap-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#6FEDD6] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                                onClick={() => setReminderScript({ id: script.id, title: script.title })}
                              >
                                <Bell className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("notifications.reminder.title")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#4DA6FF] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                          onClick={() => navigate(`/editor/${script.id}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                          onClick={() => {
                            if (isTrialExpired) {
                              setShowSubscriptionModal(true);
                            } else {
                              navigate(`/teleprompter/${script.id}`);
                            }
                          }}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 border-2 border-black rounded-lg bg-white hover:bg-[#FF6B9D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("dashboard.scripts.deleteConfirm.title")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("dashboard.scripts.deleteConfirm.description")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("dashboard.scripts.deleteConfirm.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteScript(script.id)}>
                                {t("dashboard.scripts.deleteConfirm.confirm")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] whitespace-nowrap"
                >
                  {t("dashboard.scripts.previous")}
                </Button>
                <span className="flex items-center px-4 text-sm">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] whitespace-nowrap"
                >
                  {t("dashboard.scripts.next")}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Script Reminder Dialog */}
      <ScriptReminderDialog
        open={!!reminderScript}
        onOpenChange={(open) => !open && setReminderScript(null)}
        scriptId={reminderScript?.id || ''}
        scriptTitle={reminderScript?.title || ''}
      />

      {/* Subscription Required Modal */}
      <SubscriptionRequiredModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
      />
    </Card>
  );
}
